// @ts-check
import React, { useEffect } from "react";
import { Formik } from "formik";
import { Skeleton } from "@chakra-ui/react";

import { SearchableSelectField } from "components/Form";

import useGetAccountInfo from "api/accounts/useGetAccountInfo";

/**
 * AccountSelector
 * @description
 * Component displayed at the top left of transaction history. This component
 * gives the end user the ability to select which account's transaction history they want to view.
 * Only one account's transaction history may be viewed at once for now, hence this is a single select.
 * @param {object} props
 * @param {string} props.value The account ID that is currently selected.
 * @param {string} props.onChange Callback function to give account ID back to parent component on selection (required).
 * @returns JSX.Element
 */

const AccountSelector = ({ value, onChange }) => {
  const { data, isLoading, isError } = useGetAccountInfo();

  let mainAccount = null;
  let accounts = [];

  if (data) {
    mainAccount = { label: data.payload?.username, value: data.payload?.account_id };
    const mergedAccounts = data.payload?.tags
      ?.filter((tag) => tag.namespace === "account_merge" && tag.tag === "merged_from") // only get accounts that are merged to the 'root' account
      .map((otherAccount) => ({
        label: otherAccount.resource_id,
        value: otherAccount.resource_id,
      }));

    accounts = [mainAccount, ...mergedAccounts];
  }

  // if there is no value from parent, then notify parent component
  // the main accounts ID. This way they can store it as value
  useEffect(() => {
    if (!value && mainAccount) {
      onChange(mainAccount.value);
    }
  }, [mainAccount, value, onChange]);

  if (isLoading) {
    return <Skeleton role="loading" height="20px" />; // eslint-disable-line jsx-a11y/aria-role
  }

  // if there is an API error, return nothing
  // so that txn history is still usable.
  if (isError) {
    return null;
  }

  // case there is at least one account ID. User can select which account to view.
  // one account ID is guaranteed as that's the logged in users
  return (
    <Formik initialValues={{ account: value }} enableReinitialize>
      <SearchableSelectField
        name="account"
        options={accounts}
        onChange={onChange}
        isReadOnly={accounts.length === 1}
      />
    </Formik>
  );
};

export default AccountSelector;
